/**
 * EventDetailModal Component
 *
 * Modal displaying full details of an HMC event including:
 * - Event date, time, opponent, venue
 * - Home/Away status
 * - Broadcast information
 * - Enrichment status with glow effects
 * - YouTube and event URLs
 * - Score (if available)
 * - Special event notes
 *
 * Supports:
 * - Double-click (desktop) and long-press (mobile) to open
 * - Keyboard navigation (Escape to close)
 * - Mobile-first responsive design
 * - Theme-aware styling
 * - Desktop: Non-modal draggable window with panel mode option
 */

import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import {
  X,
  Copy,
  Check,
  Calendar,
  Clock,
  Youtube,
  ExternalLink,
  Home,
  Navigation,
  Trophy,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  XCircle,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Users,
  Ticket,
  Music,
  FileText,
  DoorOpen,
  GripVertical,
  PanelRightOpen,
  PanelRightClose,
  Move,
  Pencil,
  Save,
  Loader2,
  Brain,
} from 'lucide-react';
import { apiClient } from '@shared/services/apiClient';
import { useIsMobile } from '@shared/hooks';
import { Portal } from '@shared/components/ui/Portal';
import type { HMCEvent, MusicGenre } from '../hmcEventsStore';
import { parseAttendance, getTicketsRemaining, getTicketCapacitySplit, getCleanEventName, parseEventName, type ParsedAttendance, type TicketCapacitySplit } from '../utils/attendanceParser';
import { SportsStatsWidget } from './SportsStatsWidget';
import { ArtistInfoWidget } from './ArtistInfoWidget';
import { TicketRequestModal } from './TicketRequestModal';
import { FeaturedTracksPanel } from './FeaturedTracksPanel';
import { CulturalIntelligencePanel } from './CulturalIntelligencePanel';
import { ImageUploadButton } from './ImageUploadButton';
import { useHMCEventsStore } from '../hmcEventsStore';

// Panel mode type for desktop
type PanelMode = 'floating' | 'docked-right';

// Genre display configuration (matches EventReportCard)
// Using white text with darker backgrounds for WCAG contrast compliance
const GENRE_STYLES: Record<MusicGenre, { bg: string; text: string; border: string; label: string }> = {
  rap: { bg: 'bg-amber-600/80', text: 'text-white', border: 'border-amber-500', label: 'Hip-Hop' },
  rnb: { bg: 'bg-purple-600/80', text: 'text-white', border: 'border-purple-500', label: 'R&B' },
  latino: { bg: 'bg-red-600/80', text: 'text-white', border: 'border-red-500', label: 'Latino' },
  rock: { bg: 'bg-slate-600/80', text: 'text-white', border: 'border-slate-500', label: 'Rock' },
  pop: { bg: 'bg-pink-600/80', text: 'text-white', border: 'border-pink-500', label: 'Pop' },
  country: { bg: 'bg-yellow-600/80', text: 'text-white', border: 'border-yellow-500', label: 'Country' },
  electronic: { bg: 'bg-cyan-600/80', text: 'text-white', border: 'border-cyan-500', label: 'EDM' },
  other: { bg: 'bg-dark-surface/50/80', text: 'text-white', border: 'border-gray-500', label: 'Other' },
};

interface EventDetailModalProps {
  event: HMCEvent;
  isOpen: boolean;
  onClose: () => void;
  /** Current event index (0-based) in the displayed list */
  currentIndex?: number;
  /** Total number of events in the displayed list */
  totalEvents?: number;
  /** Callback to navigate to previous event */
  onPrevious?: () => void;
  /** Callback to navigate to next event */
  onNext?: () => void;
  /** Whether this event is new (not in previous reports) */
  isNewEvent?: boolean;
  /** Total capacity for the event (if known) */
  totalCapacity?: number;
  /** All available events for backup selection */
  allEvents?: HMCEvent[];
  /** Callback when panel docked state changes (desktop only) */
  onPanelStateChange?: (isDocked: boolean, panelWidth: number) => void;
  /** Whether guest view mode is active */
  isGuestView?: boolean;
  /** Maximum tickets to show in guest mode */
  maxGuestTickets?: number;
  /** Callback when event data is updated (e.g., description edit) */
  onEventUpdate?: (eventId: number, updates: Partial<HMCEvent>) => void;
  /** Callback to open assignment modal for PC/EB section editing (admin only) */
  onEditAssignments?: (event: HMCEvent) => void;
}

/**
 * Extract YouTube video ID from various URL formats
 */
const extractYouTubeVideoId = (url: string | null): string | null => {
  if (!url) return null;

  // Match various YouTube URL patterns:
  // - https://www.youtube.com/watch?v=VIDEO_ID
  // - https://youtu.be/VIDEO_ID
  // - https://www.youtube.com/embed/VIDEO_ID
  // - https://www.youtube.com/v/VIDEO_ID
  // - https://www.youtube.com/results?search_query=... (search URLs - return null)
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

/**
 * Get event theme based on event type (for hero image gradient)
 */
interface EventTheme {
  gradient: string;
}

const getEventTheme = (event: HMCEvent): EventTheme => {
  const opponent = (event.opponent || '').toLowerCase();
  const eventType = (event.event_type || '').toLowerCase();

  // Dynamo - Orange theme
  if (eventType === 'dynamo' || opponent.includes('dynamo')) {
    return { gradient: 'from-orange-600 to-orange-800' };
  }

  // Dash - Sky blue theme
  if (eventType === 'dash' || opponent.includes('dash')) {
    return { gradient: 'from-sky-600 to-sky-800' };
  }

  // Rockets - Red theme
  if (eventType === 'rockets' || opponent.includes('rockets') || opponent.includes('lakers') || opponent.includes('warriors')) {
    return { gradient: 'from-red-600 to-red-800' };
  }

  // Concert - Purple theme
  if (event.music_genre && event.music_genre !== 'other') {
    return { gradient: 'from-purple-600 to-purple-800' };
  }

  // Default - Brand theme
  return { gradient: 'from-brand-600 to-brand-800' };
};

/**
 * Get event category for display
 */
const getEventCategory = (event: HMCEvent): string => {
  const opponent = (event.opponent || '').toLowerCase();
  const eventType = (event.event_type || '').toLowerCase();

  if (eventType === 'dynamo' || eventType === 'dash') return 'Sports';
  if (opponent.includes('rockets')) return 'Sports';
  if (event.music_genre && event.music_genre !== 'other') return 'Concert';
  if (opponent.includes('comedy') || opponent.includes('comedian')) return 'Comedy';
  if (opponent.includes('wrestling') || opponent.includes('wwe')) return 'Wrestling';
  if (opponent.includes('disney') || opponent.includes('sesame')) return 'Family';
  if (opponent.includes('rodeo')) return 'Rodeo';

  return 'Event';
};

/**
 * YouTube Embed Player Component
 *
 * Thumbnail quality hierarchy (most reliable first):
 * - hqdefault.jpg (480x360) - Always available
 * - mqdefault.jpg (320x180) - Always available
 * - maxresdefault.jpg (1280x720) - Only for HD videos, returns grey placeholder if unavailable
 *
 * We use hqdefault as primary since it's reliable and good quality for most displays.
 */
// YouTube thumbnail quality fallback chain - not all videos have all qualities
const YOUTUBE_THUMBNAIL_QUALITIES = ['hqdefault.jpg', 'mqdefault.jpg', 'default.jpg'];

const YouTubeEmbed: React.FC<{ videoId: string; title?: string }> = ({ videoId, title }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  // Use i.ytimg.com CDN for better reliability
  const [qualityIndex, setQualityIndex] = React.useState(0);
  const imgSrc = `https://i.ytimg.com/vi/${videoId}/${YOUTUBE_THUMBNAIL_QUALITIES[qualityIndex]}`;
  const [showPlaceholder, setShowPlaceholder] = React.useState(false);

  // Reset state when videoId changes (when navigating between events)
  React.useEffect(() => {
    setIsPlaying(false);
    setQualityIndex(0);
    setShowPlaceholder(false);
  }, [videoId]);

  // Handle thumbnail load error - try next quality level
  const handleImageError = React.useCallback(() => {
    if (qualityIndex < YOUTUBE_THUMBNAIL_QUALITIES.length - 1) {
      setQualityIndex(prev => prev + 1);
    } else {
      setShowPlaceholder(true);
    }
  }, [qualityIndex]);

  if (isPlaying) {
    return (
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`}
          title={title || 'YouTube video player'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsPlaying(true)}
      className="relative w-full aspect-video rounded-lg overflow-hidden group cursor-pointer bg-black touch-manipulation"
      aria-label={`Play video: ${title || 'YouTube video'}`}
    >
      {/* Thumbnail */}
      {showPlaceholder ? (
        <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center">
          <svg className="w-16 h-16 text-white/60" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        </div>
      ) : (
        <img
          src={imgSrc}
          alt={title || 'Video thumbnail'}
          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105 group-active:scale-105"
          onError={handleImageError}
          draggable={false}
        />
      )}

      {/* Dark overlay on hover/active */}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 group-active:bg-black/40 transition-all duration-150" />

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-active:scale-110 transition-transform">
          <svg className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>

      {/* YouTube logo */}
      <div className="absolute bottom-3 right-3 bg-black/60 rounded px-2 py-1 flex items-center gap-1.5">
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
        <span className="text-white text-xs font-medium">YouTube</span>
      </div>
    </button>
  );
};

/**
 * Format date for display
 * Parses date-only strings (YYYY-MM-DD) as local dates to avoid timezone shifts
 */
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'Not specified';
  try {
    // Parse as local date components to avoid UTC timezone shift
    // When JS parses "2025-04-09" it treats it as UTC midnight, which becomes
    // April 8th in western timezones. By parsing components, we treat it as local.
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format time for display
 */
const formatTime = (timeStr: string | null): string => {
  if (!timeStr) return 'TBD';
  return timeStr;
};

/**
 * Calculate doors open time based on event type
 * - Dynamo games: 2 hours before kickoff
 * - Dash games: 1.5 hours before kickoff
 * - Toyota Center events: Use scraped doors_open field
 */
const calculateDoorsOpen = (
  doorsOpen: string | null | undefined,
  kickoffTime: string | null | undefined,
  eventStartTime: string | null | undefined,
  eventType: string | null | undefined
): string | null => {
  // If we have a scraped doors_open value, use it
  if (doorsOpen) return doorsOpen;

  // Get the event time (prefer scraped start time, fall back to kickoff)
  const eventTime = eventStartTime || kickoffTime;
  if (!eventTime) return null;

  // Calculate based on event type
  const type = (eventType || '').toLowerCase();
  let hoursOffset = 0;

  if (type === 'dynamo') {
    hoursOffset = 2; // Dynamo: gates open 2 hours before
  } else if (type === 'dash') {
    hoursOffset = 1.5; // Dash: gates open 1.5 hours before
  } else {
    // For other events without scraped doors_open, don't show calculated time
    return null;
  }

  // Parse time and calculate doors open
  // Handle formats like "7:30 PM", "19:30", "7:30"
  const timeMatch = eventTime.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
  if (!timeMatch) return null;

  let hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2] || '0', 10);
  const period = (timeMatch[3] || '').toUpperCase();

  // Convert to 24-hour format if AM/PM specified
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  // Calculate doors open time
  const totalMinutes = hours * 60 + minutes - (hoursOffset * 60);
  let doorsHours = Math.floor(totalMinutes / 60);
  const doorsMinutes = totalMinutes % 60;

  // Handle negative hours (wrap around)
  if (doorsHours < 0) doorsHours += 24;

  // Format with AM/PM
  const isPM = doorsHours >= 12;
  const displayHours = doorsHours > 12 ? doorsHours - 12 : (doorsHours === 0 ? 12 : doorsHours);
  const displayMinutes = doorsMinutes.toString().padStart(2, '0');

  return `${displayHours}:${displayMinutes} ${isPM ? 'PM' : 'AM'}`;
};

/**
 * Detail Row Component
 */
const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}> = ({ icon, label, value, highlight }) => {
  const isEmpty = !value || value === 'Not specified' || value === 'TBD';

  return (
    <div className="flex items-start gap-3 py-3 border-b border-dark-border/30 last:border-b-0">
      <div className="flex-shrink-0 text-dark-muted mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-dark-muted uppercase tracking-wide mb-0.5">
          {label}
        </p>
        <div
          className={`text-sm ${
            isEmpty
              ? 'text-dark-muted/70 italic'
              : highlight
              ? 'text-dark-text font-semibold'
              : 'text-dark-text font-medium'
          }`}
        >
          {value || 'Not specified'}
        </div>
      </div>
    </div>
  );
};

/**
 * Link Row Component
 */
const LinkRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  url: string | null;
  displayText?: string;
  className?: string;
}> = ({ icon, label, url, displayText, className }) => (
  <div className="flex items-start gap-3 py-3 border-b border-dark-border/30 last:border-b-0">
    <div className="flex-shrink-0 text-dark-muted mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-dark-muted uppercase tracking-wide mb-0.5">
        {label}
      </p>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-sm font-medium hover:underline inline-flex items-center gap-1.5 min-h-[44px] ${
            className || ''
          }`}
          style={{ color: 'var(--accent-primary)' }}
        >
          {displayText || 'View Link'}
          <ExternalLink size={14} />
        </a>
      ) : (
        <span className="text-sm text-dark-muted/70 italic">Not available</span>
      )}
    </div>
  </div>
);

/**
 * Section Card Component
 */
const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  highlight?: boolean;
}> = ({ title, children, highlight }) => (
  <div
    className={`rounded-xl shadow-sm p-4 ${
      highlight
        ? 'bg-emerald-500/10 border-2 border-emerald-500/30'
        : 'bg-dark-surface border border-dark-border/30'
    }`}
  >
    <h3
      className={`text-xs font-bold uppercase tracking-wider mb-3 pb-2 border-b ${
        highlight
          ? 'text-emerald-400 border-emerald-500/30'
          : 'text-dark-text border-dark-border/30'
      }`}
    >
      {title}
    </h3>
    {children}
  </div>
);

/**
 * Status Badge Component with optional glow
 */
/**
 * Status Badge Component with optional glow
 * Uses white text with darker backgrounds for WCAG contrast compliance
 */
const StatusBadge: React.FC<{
  status: string;
  type: 'enriched' | 'pending' | 'failed' | 'skipped' | 'home' | 'away' | 'dynamo' | 'dash' | 'general';
}> = ({ status, type }) => {
  const getStyles = () => {
    switch (type) {
      case 'enriched':
        return {
          className: 'bg-green-600/80 text-white border-green-500 hmc-enriched-glow',
          icon: <CheckCircle2 size={14} />,
        };
      case 'pending':
        return {
          className: 'bg-yellow-600/80 text-white border-yellow-500',
          icon: <Clock size={14} />,
        };
      case 'failed':
        return {
          className: 'bg-red-600/80 text-white border-red-500',
          icon: <XCircle size={14} />,
        };
      case 'skipped':
        return {
          className: 'bg-dark-surface/50/80 text-white border-gray-500',
          icon: <SkipForward size={14} />,
        };
      case 'home':
        return {
          className: 'bg-green-600/80 text-white border-green-500',
          icon: <Home size={14} />,
        };
      case 'away':
        return {
          className: 'bg-purple-600/80 text-white border-purple-500',
          icon: <Navigation size={14} />,
        };
      case 'dynamo':
        return {
          className: 'bg-orange-600/80 text-white border-orange-500',
          icon: <Sparkles size={14} />,
        };
      case 'dash':
        return {
          className: 'bg-sky-600/80 text-white border-sky-500',
          icon: <Sparkles size={14} />,
        };
      default:
        return {
          className: 'bg-dark-surface/50/80 text-white border-gray-500',
          icon: <AlertCircle size={14} />,
        };
    }
  };

  const styles = getStyles();

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border ${styles.className}`}
    >
      {styles.icon}
      {status}
    </span>
  );
};

/**
 * EventDetailModal Component
 */
export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  isOpen,
  onClose,
  currentIndex,
  totalEvents,
  onPrevious,
  onNext,
  isNewEvent = false,
  totalCapacity,
  allEvents = [],
  onPanelStateChange,
  isGuestView = false,
  maxGuestTickets = 4,
  onEventUpdate,
  onEditAssignments,
}) => {
  const [copied, setCopied] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const isMobile = useIsMobile();

  // Store access for image updates
  const { updateEvent } = useHMCEventsStore();

  // Image upload state
  const [isUpdatingImage, setIsUpdatingImage] = useState(false);

  // Description editing state (admin only)
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(event.event_description || '');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  // Saved assignments from database (syncs with TicketAssignmentModal)
  // Includes section_source to indicate if assignment came from memory
  const [savedAssignments, setSavedAssignments] = useState<Record<string, { section: string; source: string | null }>>({});

  // Reset editing state when event changes
  useEffect(() => {
    setIsEditingDescription(false);
    setEditedDescription(event.event_description || '');
    setDescriptionError(null);
  }, [event.id, event.event_description]);

  // Generate stable event key for fetching assignments (same pattern as TicketAssignmentModal)
  const eventKey = useMemo(() => {
    if (!event) return '';
    const eventType = (event.event_type || 'general').toLowerCase();
    const eventDate = event.event_date?.split('T')[0] || 'unknown';
    return `${eventType}_${eventDate}`;
  }, [event?.event_type, event?.event_date]);

  // Fetch saved assignments from database with memory fallback (syncs with TicketAssignmentModal)
  useEffect(() => {
    if (!eventKey || !isOpen) {
      setSavedAssignments({});
      return;
    }

    // Response type for memory-aware assignment endpoint
    interface AssignmentWithMemory {
      attendee_name: string;
      section: 'PC' | 'EB' | null;
      section_source: 'stored' | 'memory' | null;
      memory_confidence: number | null;
    }

    apiClient.get<{
      event_key: string;
      assignments: AssignmentWithMemory[];
    }>(`/hmc/assignments/${encodeURIComponent(eventKey)}/with-memory`)
      .then(response => {
        const assignmentMap: Record<string, { section: string; source: string | null }> = {};
        (response.data.assignments || []).forEach((a) => {
          // Use lowercase key for case-insensitive matching
          if (a.section) {
            assignmentMap[a.attendee_name.toLowerCase().trim()] = {
              section: a.section,
              source: a.section_source || null,
            };
          }
        });
        setSavedAssignments(assignmentMap);
      })
      .catch(() => {
        // No saved assignments found - that's fine
        setSavedAssignments({});
      });
  }, [eventKey, isOpen]);

  // Desktop panel mode state
  const [panelMode, setPanelMode] = useState<PanelMode>('floating');
  const [panelWidth, setPanelWidth] = useState(420); // Default width for docked panel

  // Notify parent of panel state changes (for main content resizing)
  useEffect(() => {
    if (!isMobile && onPanelStateChange) {
      const isDocked = isOpen && panelMode === 'docked-right';
      onPanelStateChange(isDocked, isDocked ? panelWidth : 0);
    }
  }, [isOpen, panelMode, panelWidth, isMobile, onPanelStateChange]);

  // Cleanup: notify parent when modal closes
  useEffect(() => {
    return () => {
      if (onPanelStateChange) {
        onPanelStateChange(false, 0);
      }
    };
  }, [onPanelStateChange]);

  // Drag state for floating window
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizingPanel, setIsResizingPanel] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  // Center the floating window on open
  useEffect(() => {
    if (isOpen && !isMobile && panelMode === 'floating') {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const modalWidth = 640; // max-w-2xl equivalent
      const modalHeight = window.innerHeight * 0.9;
      setPosition({
        x: Math.max(0, (windowWidth - modalWidth) / 2),
        y: Math.max(0, (windowHeight - modalHeight) / 2),
      });
    }
  }, [isOpen, isMobile, panelMode]);

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (isMobile || panelMode !== 'floating') return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    positionRef.current = position;
  }, [isMobile, panelMode, position]);

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragStartRef.current.x;
      const newY = e.clientY - dragStartRef.current.y;
      // Constrain to viewport
      const maxX = window.innerWidth - 100;
      const maxY = window.innerHeight - 100;
      setPosition({
        x: Math.max(-100, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle panel resize for docked mode
  const handlePanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingPanel(true);
  }, []);

  useEffect(() => {
    if (!isResizingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setPanelWidth(Math.max(320, Math.min(newWidth, 800)));
    };

    const handleMouseUp = () => {
      setIsResizingPanel(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingPanel]);

  // Toggle between panel modes
  const togglePanelMode = useCallback(() => {
    setPanelMode((prev) => (prev === 'floating' ? 'docked-right' : 'floating'));
  }, []);

  // Parse attendance data and merge with saved assignments from database
  const attendance = useMemo(() => {
    const parsed = parseAttendance(event.special_event);

    // Merge with saved assignments (database takes precedence over parsed sections)
    const mergedEntries = parsed.entries.map(entry => {
      const nameKey = entry.name.toLowerCase().trim();
      const savedAssignment = savedAssignments[nameKey];
      return {
        ...entry,
        // Database assignment takes precedence, then parsed section, then undefined
        section: savedAssignment?.section?.toUpperCase() as 'PC' | 'EB' | undefined || entry.section,
        // Track if this came from memory (for visual indicator)
        sectionSource: savedAssignment?.source || null,
      };
    });

    return { ...parsed, entries: mergedEntries };
  }, [event.special_event, savedAssignments]);

  // Parse event name to extract clean display name and attendance info
  const eventNameParts = useMemo(() => parseEventName(event.opponent), [event.opponent]);
  const cleanEventName = eventNameParts.cleanName || 'TBD';

  // Calculate tickets remaining using venue-specific capacity
  const ticketInfo = useMemo(() =>
    getTicketsRemaining(event.special_event, event.opponent, event.event_type, event.venue),
    [event.special_event, event.opponent, event.event_type, event.venue]
  );

  // Calculate PC/EB capacity split (50/50)
  const capacitySplit = useMemo(() =>
    getTicketCapacitySplit(event.special_event, event.opponent, event.event_type, event.venue),
    [event.special_event, event.opponent, event.event_type, event.venue]
  );

  // Filter attendance entries by section (using merged data from DB + parsed)
  const pcEntries = useMemo(() =>
    attendance.entries.filter(e => e.section?.toUpperCase() === 'PC'),
    [attendance.entries]
  );
  const ebEntries = useMemo(() =>
    attendance.entries.filter(e => e.section?.toUpperCase() === 'EB'),
    [attendance.entries]
  );
  const unsectionedEntries = useMemo(() =>
    attendance.entries.filter(e => !e.section),
    [attendance.entries]
  );

  // Determine actual event type for display (Sports, Concert, etc.)
  const displayEventType = useMemo(() => {
    const eventType = event.event_type?.toLowerCase() || '';
    const opponentLower = cleanEventName.toLowerCase();

    // Sports events (Dynamo, Dash, Rockets, NBA, MLS, etc.)
    if (eventType === 'dynamo' || opponentLower.includes('dynamo')) {
      return { label: 'Sports', category: 'dynamo' as const };
    }
    if (eventType === 'dash' || opponentLower.includes('dash')) {
      return { label: 'Sports', category: 'dash' as const };
    }
    if (eventType === 'rockets' || opponentLower.includes('rockets') ||
        (opponentLower.includes(' v ') && !opponentLower.includes('tour')) ||
        (opponentLower.includes(' vs ') && !opponentLower.includes('tour'))) {
      return { label: 'Sports', category: 'general' as const };
    }

    // Concerts - check for music genre OR tour/concert keywords
    if (event.music_genre) {
      return { label: 'Concert', category: 'general' as const };
    }
    if (opponentLower.includes('tour') || opponentLower.includes('concert') ||
        opponentLower.includes('live:') || opponentLower.includes('live in')) {
      return { label: 'Concert', category: 'general' as const };
    }

    // Comedy
    if (opponentLower.includes('comedy') || opponentLower.includes('comedian')) {
      return { label: 'Comedy', category: 'general' as const };
    }

    // Family events
    if (opponentLower.includes('disney') || opponentLower.includes('sesame') ||
        opponentLower.includes('paw patrol') || opponentLower.includes('circus') ||
        opponentLower.includes('monster truck')) {
      return { label: 'Family', category: 'general' as const };
    }

    // Wrestling
    if (opponentLower.includes('wrestling') || opponentLower.includes('wwe') ||
        opponentLower.includes('aew')) {
      return { label: 'Wrestling', category: 'general' as const };
    }

    // Rodeo
    if (opponentLower.includes('rodeo')) {
      return { label: 'Rodeo', category: 'general' as const };
    }

    // Default - use event_type or "Event"
    if (eventType && eventType !== 'general') {
      return { label: eventType.charAt(0).toUpperCase() + eventType.slice(1), category: 'general' as const };
    }
    return { label: 'Event', category: 'general' as const };
  }, [event.event_type, event.music_genre, cleanEventName]);

  // Check if event is fully booked
  const isFullyBooked = useMemo(() => {
    // Use provided totalCapacity if available, otherwise use auto-detected capacity
    const effectiveCapacity = totalCapacity || ticketInfo.capacity;
    return attendance.totalTickets >= effectiveCapacity;
  }, [attendance.totalTickets, totalCapacity, ticketInfo.capacity]);

  // Calculate doors open time (from scraping or calculated for Dynamo/Dash)
  const doorsOpenTime = useMemo(() =>
    calculateDoorsOpen(event.doors_open, event.kickoff_time, event.event_start_time, event.event_type),
    [event.doors_open, event.kickoff_time, event.event_start_time, event.event_type]
  );

  // Navigation state
  const hasNavigation =
    currentIndex !== undefined && totalEvents !== undefined && totalEvents > 0;
  const canGoPrevious = hasNavigation && currentIndex > 0;
  const canGoNext = hasNavigation && currentIndex < totalEvents - 1;

  /**
   * Handle keyboard navigation
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && canGoPrevious && onPrevious) {
        e.preventDefault();
        onPrevious();
      } else if (e.key === 'ArrowRight' && canGoNext && onNext) {
        e.preventDefault();
        onNext();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Only lock body scroll on mobile (modal behavior)
      // Desktop is non-modal, so don't lock scroll
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (isMobile) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, onClose, canGoPrevious, canGoNext, onPrevious, onNext, isMobile]);

  /**
   * Copy event details to clipboard
   */
  const handleCopy = useCallback(async () => {
    const text = `
HMC Event: ${event.opponent || 'TBD'}
==============================

Date: ${formatDate(event.event_date)}
Time: ${formatTime(event.kickoff_time)}
Location: ${event.is_home ? 'Home' : 'Away'}
Venue: ${event.venue || 'TBD'}
Event Type: ${event.event_type}
Broadcast: ${event.broadcast || 'Not specified'}
${event.score ? `Score: ${event.score}` : ''}
${event.special_event ? `Special Event: ${event.special_event}` : ''}

Links:
- Event URL: ${event.event_url || 'Not available'}
- YouTube: ${event.youtube_url || 'Not available'}

Enrichment Status: ${event.enrichment_status || 'Not enriched'}
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [event]);

  /**
   * Save edited description to backend
   */
  const handleSaveDescription = useCallback(async () => {
    if (isSavingDescription) return;

    setIsSavingDescription(true);
    setDescriptionError(null);

    try {
      await apiClient.patch(`/houston-calendar/events/${event.id}`, {
        event_description: editedDescription,
      });

      // Update local state
      setIsEditingDescription(false);

      // Notify parent of update
      if (onEventUpdate) {
        onEventUpdate(event.id, { event_description: editedDescription });
      }
    } catch (err) {
      console.error('Failed to save description:', err);
      setDescriptionError('Failed to save. Please try again.');
    } finally {
      setIsSavingDescription(false);
    }
  }, [event.id, editedDescription, isSavingDescription, onEventUpdate]);

  /**
   * Cancel description editing
   */
  const handleCancelDescription = useCallback(() => {
    setIsEditingDescription(false);
    setEditedDescription(event.event_description || '');
    setDescriptionError(null);
  }, [event.event_description]);

  /**
   * Handle image URL update (from ImageUploadButton)
   */
  const handleImageChange = useCallback(async (imageUrl: string | null) => {
    setIsUpdatingImage(true);
    try {
      await updateEvent(event.id, { event_image_url: imageUrl });
      // Notify parent of update if callback exists
      if (onEventUpdate) {
        onEventUpdate(event.id, { event_image_url: imageUrl });
      }
    } finally {
      setIsUpdatingImage(false);
    }
  }, [event.id, updateEvent, onEventUpdate]);

  if (!isOpen) return null;

  // Docked right panel mode (desktop only)
  if (!isMobile && panelMode === 'docked-right') {
    return (
      <Portal>
        {/* Docked Right Panel */}
        <div
          ref={modalRef}
          className="fixed top-0 right-0 h-full bg-dark-surface border-l border-dark-border/30 shadow-2xl flex flex-col"
          style={{ width: `${panelWidth}px`, zIndex: 50 }}
          role="dialog"
          aria-modal="false"
          aria-labelledby="event-modal-title"
          data-testid="event-detail-modal"
        >
          {/* Resize handle */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-brand-500/50 transition-all duration-150 group"
            onMouseDown={handlePanelResizeStart}
          >
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical size={16} className="text-dark-muted" />
            </div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg px-4 py-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Navigation buttons */}
              {hasNavigation && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={onPrevious}
                    disabled={!canGoPrevious}
                    className="p-1.5 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-md transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed min-w-[32px] min-h-[32px] flex items-center justify-center"
                    aria-label="Previous event"
                    data-testid="event-detail-prev-button"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={onNext}
                    disabled={!canGoNext}
                    className="p-1.5 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-md transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed min-w-[32px] min-h-[32px] flex items-center justify-center"
                    aria-label="Next event"
                    data-testid="event-detail-next-button"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2
                    id="event-modal-title"
                    className="font-bold text-white truncate leading-tight text-base"
                  >
                    {cleanEventName}
                  </h2>
                  {hasNavigation && (
                    <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-dark-muted bg-dark-surface/50 border border-dark-border/30 rounded-full">
                      {currentIndex + 1} of {totalEvents}
                    </span>
                  )}
                </div>
                <p className="text-xs text-dark-muted mt-0.5">
                  {formatDate(event.event_date)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Panel mode toggle */}
              <button
                onClick={togglePanelMode}
                className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center"
                aria-label="Switch to floating window"
                title="Switch to floating window"
                data-testid="event-detail-toggle-mode"
              >
                <PanelRightClose size={18} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center"
                aria-label="Close panel"
                data-testid="event-detail-close-button"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body - scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {renderModalContent()}
          </div>

          {/* Footer */}
          <div className="border-t border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg px-4 py-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-dark-muted">Event ID: {event.id}</p>
              <button
                onClick={() => setTicketModalOpen(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-white text-sm font-medium rounded-lg transition-all duration-150 min-h-[36px] ${
                  isFullyBooked
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-brand-500 hover:bg-brand-600'
                }`}
                data-testid="event-detail-ticket-button"
              >
                <Ticket size={14} />
                {isFullyBooked ? 'Standby' : 'Request'}
              </button>
            </div>
          </div>
        </div>

        {/* Ticket Request Modal */}
        <TicketRequestModal
          isOpen={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          event={event}
          allEvents={allEvents}
          isStandbyRequest={isFullyBooked}
        />
      </Portal>
    );
  }

  // Helper function to render modal content (shared between modes)
  function renderModalContent() {
    const theme = getEventTheme(event);
    const category = getEventCategory(event);
    const videoId = extractYouTubeVideoId(event.youtube_url);

    return (
      <>
        {/* Hero Image Section - Priority: event_image_url > gradient placeholder */}
        {/* YouTube moved to bottom for better UX */}
        <div className="mb-4 relative group">
          {event.event_image_url ? (
            /* Show event image (team logo, scraped image) on gradient background */
            <div className={`w-full aspect-video rounded-xl overflow-hidden bg-gradient-to-br ${theme.gradient} flex items-center justify-center p-8`}>
              <img
                src={event.event_image_url}
                alt={cleanEventName}
                className="max-w-full max-h-full object-contain drop-shadow-lg"
                onError={(e) => {
                  // Hide image if it fails to load
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            /* Gradient placeholder with icon */
            <div className={`w-full aspect-video rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center`}>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-3 bg-dark-surface/20 rounded-full flex items-center justify-center">
                  {category === 'Concert' ? (
                    <Music className="w-8 h-8 text-white" />
                  ) : (
                    <Calendar className="w-8 h-8 text-white" />
                  )}
                </div>
                <p className="text-white/80 text-sm font-medium">{category}</p>
              </div>
            </div>
          )}
          {/* Image Upload Button - Admin only */}
          <ImageUploadButton
            currentImageUrl={event.event_image_url}
            onImageChange={handleImageChange}
            isLoading={isUpdatingImage}
            overlayMode={true}
            isGuestView={isGuestView}
          />
        </div>

        {/* Status Badges Row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {/* New Event Badge */}
          {isNewEvent && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-brand-600/80 text-white border border-brand-500">
              <Sparkles size={14} />
              NEW
            </span>
          )}
          {/* USI Suite Badge - ALL events in this report are USI Suite events */}
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-gradient-to-r from-brand-500 to-brand-600 text-white border border-brand-400 shadow-sm"
            title="USI has a suite at this venue"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14h-2v-4H8v-2h2V9h2v2h2v2h-2v4zm4-8h-2V7h2v2z"/>
            </svg>
            {event.venue?.toLowerCase().includes('shell stadium') || event.event_type === 'dynamo' || event.event_type === 'dash'
              ? 'USI Shell Stadium Suite'
              : 'USI Toyota Center Suite'}
          </span>
          {/* Music Genre Badge */}
          {event.music_genre && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border ${
                GENRE_STYLES[event.music_genre].bg
              } ${GENRE_STYLES[event.music_genre].text} ${GENRE_STYLES[event.music_genre].border}`}
            >
              <Music size={14} />
              {GENRE_STYLES[event.music_genre].label}
            </span>
          )}
          {/* Fully Booked Badge */}
          {isFullyBooked && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-red-600/80 text-white border border-red-500">
              <XCircle size={14} />
              SOLD OUT
            </span>
          )}
          {/* Event Type Badge */}
          <StatusBadge
            status={displayEventType.label}
            type={displayEventType.category}
          />
        </div>

        {/* Sports Stats Widget */}
        {(event.event_type === 'dynamo' || event.event_type === 'dash' || event.event_type === 'rockets' ||
          event.opponent?.toLowerCase().startsWith('rockets') ||
          (event.venue?.toLowerCase().includes('toyota center') && event.event_type !== 'concert')) && (
          <div className="mb-4">
            <SportsStatsWidget
              eventType={event.opponent?.toLowerCase().startsWith('rockets') ? 'rockets' : event.event_type}
              opponent={event.opponent}
            />
          </div>
        )}

        {/* Event Details Section */}
        <div className="space-y-4">
          <SectionCard title="Event Details">
            <DetailRow
              icon={<Calendar size={16} />}
              label="Date"
              value={formatDate(event.event_date)}
              highlight
            />
            <DetailRow
              icon={<Clock size={16} />}
              label={event.event_start_time ? 'Start Time' : 'Kickoff Time'}
              value={
                event.event_start_time ? (
                  <span className="text-emerald-400 font-semibold">{event.event_start_time}</span>
                ) : (
                  formatTime(event.kickoff_time)
                )
              }
              highlight
            />
            {event.event_start_time && event.kickoff_time && event.kickoff_time !== event.event_start_time && (
              <DetailRow
                icon={<Clock size={16} />}
                label="Original Time (Schedule)"
                value={formatTime(event.kickoff_time)}
              />
            )}
            {doorsOpenTime && (
              <DetailRow
                icon={<DoorOpen size={16} />}
                label="Doors Open"
                value={
                  <span className="text-blue-400 font-semibold">
                    {doorsOpenTime}
                    {!event.doors_open && (event.event_type === 'dynamo' || event.event_type === 'dash') && (
                      <span className="text-xs text-dark-muted ml-2">
                        ({event.event_type === 'dynamo' ? '2 hrs before' : '1.5 hrs before'})
                      </span>
                    )}
                  </span>
                }
              />
            )}
            {event.score && (
              <DetailRow
                icon={<Trophy size={16} />}
                label="Score"
                value={event.score}
                highlight
              />
            )}
            {/* Inline Links */}
            {(event.event_url || event.youtube_url) && (
              <div className="flex flex-wrap gap-2 pt-3 mt-1 border-t border-dark-border/30">
                {event.event_url && (
                  <a
                    href={event.event_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-dark-input border border-dark-border/30 rounded-xl text-sm font-medium text-white hover:bg-dark-surface/50 transition-all duration-150 min-h-[40px]"
                  >
                    <ExternalLink size={14} />
                    Event Page
                  </a>
                )}
                {event.youtube_url && (
                  <a
                    href={event.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-red-600/20 border border-red-500/50 rounded-lg text-sm font-medium text-red-400 hover:bg-red-600/30 transition-all duration-150 min-h-[40px]"
                  >
                    <Youtube size={14} />
                    {event.opponent?.toLowerCase().includes('rockets') ? 'Matchup Highlights' : 'Watch Video'}
                  </a>
                )}
              </div>
            )}
          </SectionCard>

          {/* Event Description - Editable for admin, read-only for guests */}
          {(event.event_description || !isGuestView) && (
            <SectionCard title="Event Description">
              {isGuestView ? (
                /* Guest View: Read-only */
                event.event_description ? (
                  <div className="flex items-start gap-3 py-2">
                    <div className="flex-shrink-0 text-dark-muted mt-0.5">
                      <FileText size={16} />
                    </div>
                    <p className="text-sm text-dark-text leading-relaxed">
                      {event.event_description}
                    </p>
                  </div>
                ) : null
              ) : isEditingDescription ? (
                /* Admin View: Editing Mode */
                <div className="space-y-3">
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Enter event description..."
                    className="w-full min-h-[120px] px-3 py-2 bg-dark-input border border-dark-border/30 rounded-xl text-sm text-dark-text placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
                    maxLength={2000}
                    disabled={isSavingDescription}
                    data-testid="event-description-textarea"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-dark-muted">
                      {editedDescription.length}/2000 characters
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCancelDescription}
                        disabled={isSavingDescription}
                        className="px-3 py-1.5 text-sm text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-lg transition-all duration-150 disabled:opacity-50"
                        data-testid="event-description-cancel-button"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveDescription}
                        disabled={isSavingDescription}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-all duration-150 disabled:opacity-50"
                        data-testid="event-description-save-button"
                      >
                        {isSavingDescription ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={14} />
                            Save
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  {descriptionError && (
                    <p className="text-xs text-red-400">{descriptionError}</p>
                  )}
                </div>
              ) : (
                /* Admin View: Display Mode with Edit Button */
                <div className="flex items-start gap-3 py-2">
                  <div className="flex-shrink-0 text-dark-muted mt-0.5">
                    <FileText size={16} />
                  </div>
                  <div className="flex-1">
                    {event.event_description ? (
                      <p className="text-sm text-dark-text leading-relaxed">
                        {event.event_description}
                      </p>
                    ) : (
                      <p className="text-sm text-dark-muted italic">
                        No description available
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setIsEditingDescription(true)}
                    className="flex-shrink-0 p-1.5 text-dark-muted hover:text-brand-400 hover:bg-dark-surface/50 rounded-lg transition-all duration-150"
                    title="Edit description"
                    data-testid="event-description-edit-button"
                  >
                    <Pencil size={14} />
                  </button>
                </div>
              )}
            </SectionCard>
          )}

          {/* USI Suite Information - ALL events in this report are USI Suite events */}
          <SectionCard title="USI Suite Information">
            <div className="space-y-3">
              <div className="flex items-start gap-3 py-2">
                <div className="flex-shrink-0 text-brand-400 mt-0.5">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div className="text-sm text-dark-text">
                  <p className="font-medium mb-1">Food & Beverages</p>
                  <p className="text-dark-muted">All food (dinner, snacks and appetizers) provided as well as drinks, soda, beer and wine.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 py-2 border-t border-dark-border/30">
                <div className="flex-shrink-0 text-brand-400 mt-0.5">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M9 17V7h4a3 3 0 0 1 0 6H9"/>
                  </svg>
                </div>
                <div className="text-sm text-dark-text">
                  <p className="font-medium mb-1">Parking</p>
                  {event.venue?.toLowerCase().includes('shell stadium') || event.event_type === 'dynamo' || event.event_type === 'dash' ? (
                    <p className="text-dark-muted">Parking Pass will be provided. Same setup for food and drinks.</p>
                  ) : (
                    <p className="text-dark-muted">Parking Pass in Tundra Garage (Red Level - Private Suite Access). One Parking Pass Per Guest Group Provided.</p>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Artist Info Widget */}
          {event.music_genre && event.opponent && (
            <ArtistInfoWidget
              artistName={event.opponent}
              defaultCollapsed={isMobile}
            />
          )}

          {/* Attendance Section */}
          {attendance.entries.length > 0 && (
            <SectionCard title={isGuestView ? "Ticket Availability" : "Attendance"} highlight>
              {isGuestView ? (
                /* Guest View: Only show EB available tickets (capped) */
                <div className={`flex items-center justify-between p-3 rounded-lg border ${
                  capacitySplit.ebRemaining === 0
                    ? 'bg-red-600/80 border-red-500'
                    : 'bg-green-600/80 border-green-500'
                }`}>
                  <div className="flex items-center gap-2">
                    <Ticket size={20} className="text-white" />
                    <span className="text-sm font-medium text-white">Available</span>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">
                      {capacitySplit.ebRemaining === 0 ? 'Sold Out' : Math.min(capacitySplit.ebRemaining, maxGuestTickets)}
                    </span>
                    {capacitySplit.ebRemaining > 0 && (
                      <span className="text-xs text-white/90 ml-1">tickets</span>
                    )}
                  </div>
                </div>
              ) : (
                /* Admin View: Full attendance info with PC/EB sections */
                <>
                  {/* Availability Summary - Based on EB remaining */}
                  <div className="flex gap-3 mb-4">
                    <div className="flex-1 p-3 bg-emerald-600/80 border border-emerald-500 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Ticket size={16} className="text-white" />
                        <span className="text-xs font-medium text-white/90 uppercase">Total Used</span>
                      </div>
                      <span className="text-2xl font-bold text-white">{attendance.totalTickets}</span>
                      <span className="text-xs text-white/90 ml-1">/ {capacitySplit.totalCapacity}</span>
                    </div>
                    <div className={`flex-1 p-3 rounded-lg border ${
                      capacitySplit.ebRemaining === 0
                        ? 'bg-red-600/80 border-red-500'
                        : capacitySplit.ebRemaining <= 3
                        ? 'bg-yellow-600/80 border-yellow-500'
                        : 'bg-green-600/80 border-green-500'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Ticket size={16} className="text-white" />
                        <span className="text-xs font-medium uppercase text-white/90">EB Available</span>
                      </div>
                      <span className="text-2xl font-bold text-white">{capacitySplit.ebRemaining}</span>
                      <span className="text-xs ml-1 text-white/90">/ {capacitySplit.ebCapacity}</span>
                    </div>
                  </div>

                  {/* PC Section */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between p-2 bg-purple-500/20 rounded-t-lg border border-purple-500/40">
                      <span className="text-sm font-semibold text-purple-300 uppercase tracking-wide">PC (Producer Circle)</span>
                      <span className="text-sm font-bold text-purple-300">
                        {capacitySplit.pcUsed} / {capacitySplit.pcCapacity}
                      </span>
                    </div>
                    <div className="border border-t-0 border-purple-500/30 rounded-b-lg bg-purple-500/5 p-2">
                      {pcEntries.length > 0 ? (
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {pcEntries.map((entry, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-purple-500/10 rounded">
                              <div className="flex items-center gap-2">
                                <Users size={12} className="text-purple-400" />
                                <span className="text-sm text-dark-text font-medium">{entry.name}</span>
                                {/* Memory indicator - shows when assignment came from memory */}
                                {entry.sectionSource === 'memory' && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[10px] text-amber-400/80 px-1 py-0.5 bg-amber-500/10 rounded"
                                    title="Suggested based on past attendance"
                                  >
                                    <Brain size={10} />
                                    Suggested
                                  </span>
                                )}
                                {entry.company && (
                                  <span className="text-xs text-purple-300/80 px-1.5 py-0.5 bg-purple-500/20 rounded">
                                    {entry.company}
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-bold text-purple-300">{entry.tickets}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-purple-300/60 italic py-2 text-center">
                          No PC reservations
                        </div>
                      )}
                    </div>
                  </div>

                  {/* EB Section */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between p-2 bg-blue-500/20 rounded-t-lg border border-brand-500/40">
                      <span className="text-sm font-semibold text-blue-300 uppercase tracking-wide">EB (Employee Benefits)</span>
                      <span className="text-sm font-bold text-blue-300">
                        {capacitySplit.ebUsed} / {capacitySplit.ebCapacity}
                      </span>
                    </div>
                    <div className="border border-t-0 border-brand-500/30 rounded-b-lg bg-blue-500/5 p-2">
                      {ebEntries.length > 0 ? (
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {ebEntries.map((entry, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-blue-500/10 rounded">
                              <div className="flex items-center gap-2">
                                <Users size={12} className="text-blue-400" />
                                <span className="text-sm text-dark-text font-medium">{entry.name}</span>
                                {/* Memory indicator - shows when assignment came from memory */}
                                {entry.sectionSource === 'memory' && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[10px] text-amber-400/80 px-1 py-0.5 bg-amber-500/10 rounded"
                                    title="Suggested based on past attendance"
                                  >
                                    <Brain size={10} />
                                    Suggested
                                  </span>
                                )}
                                {entry.company && (
                                  <span className="text-xs text-blue-300/80 px-1.5 py-0.5 bg-blue-500/20 rounded">
                                    {entry.company}
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-bold text-blue-300">{entry.tickets}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-blue-300/60 italic py-2 text-center">
                          No EB reservations
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Unsectioned entries (legacy data without PC/EB markers) */}
                  {unsectionedEntries.length > 0 && (
                    <div className="mb-2">
                      <div className="flex items-center justify-between p-2 bg-dark-border/30 rounded-t-lg border border-dark-border/30">
                        <span className="text-sm font-semibold text-dark-muted uppercase tracking-wide">Other</span>
                        <span className="text-sm font-bold text-dark-muted">
                          {unsectionedEntries.reduce((sum, e) => sum + e.tickets, 0)} tickets
                        </span>
                      </div>
                      <div className="border border-t-0 border-dark-border/30 rounded-b-lg bg-dark-border/10 p-2">
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {unsectionedEntries.map((entry, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-dark-border/20 rounded">
                              <div className="flex items-center gap-2">
                                <Users size={12} className="text-dark-muted" />
                                <span className="text-sm text-dark-text font-medium">{entry.name}</span>
                                {entry.company && (
                                  <span className="text-xs text-dark-muted px-1.5 py-0.5 bg-dark-border/50 rounded">
                                    {entry.company}
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-bold text-white">{entry.tickets}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </SectionCard>
          )}
          {!attendance.entries.length && attendance.cleanText && (
            <SectionCard title="Notes">
              <p className="text-sm text-dark-text">{attendance.cleanText}</p>
            </SectionCard>
          )}
          {!attendance.entries.length && !attendance.cleanText && (
            <SectionCard title="Ticket Availability">
              {isGuestView ? (
                /* Guest View: Show EB capacity */
                <div className="flex items-center justify-between p-3 bg-green-600/80 border border-green-500 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Ticket size={20} className="text-white" />
                    <span className="text-sm font-medium text-white">Available</span>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">
                      {Math.min(capacitySplit.ebCapacity, maxGuestTickets)}
                    </span>
                    <span className="text-xs text-white/90 ml-1">tickets</span>
                  </div>
                </div>
              ) : (
                /* Admin View: Show PC and EB sections with full capacity */
                <div className="space-y-3">
                  {/* PC Section */}
                  <div className="flex items-center justify-between p-2 bg-purple-500/20 rounded-lg border border-purple-500/40">
                    <span className="text-sm font-semibold text-purple-300 uppercase tracking-wide">PC (Producer Circle)</span>
                    <span className="text-sm font-bold text-purple-300">
                      0 / {capacitySplit.pcCapacity}
                    </span>
                  </div>
                  {/* EB Section */}
                  <div className="flex items-center justify-between p-2 bg-blue-500/20 rounded-lg border border-brand-500/40">
                    <span className="text-sm font-semibold text-blue-300 uppercase tracking-wide">EB (Employee Benefits)</span>
                    <span className="text-sm font-bold text-blue-300">
                      0 / {capacitySplit.ebCapacity}
                    </span>
                  </div>
                  <div className="text-xs text-dark-muted text-center capitalize">
                    Total: {capacitySplit.totalCapacity} ({capacitySplit.eventCategory})
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {/* Edit Assignments Button - Admin only, below ticket availability */}
          {!isGuestView && onEditAssignments && (
            <button
              onClick={() => onEditAssignments(event)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/20 text-purple-400 hover:bg-purple-500 hover:text-white border border-purple-500/30 rounded-lg text-sm font-medium transition-all duration-150 min-h-[44px]"
              data-testid="event-detail-edit-assignments-button"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit PC/EB Assignments
            </button>
          )}

          {/* Cultural Intelligence Panel - For concerts, adaptive based on days until event */}
          {event.music_genre && event.opponent && event.event_date && (
            <CulturalIntelligencePanel
              artistName={event.opponent}
              eventDate={event.event_date}
              accentColor="purple"
            />
          )}

          {/* Featured Tracks Panel - For concerts, Spotify previews */}
          {event.music_genre && event.opponent && (
            <FeaturedTracksPanel
              artistName={event.opponent}
              accentColor="purple"
            />
          )}

          {/* YouTube Video Section - Moved to bottom */}
          {videoId && (
            <SectionCard title={event.opponent?.toLowerCase().includes('rockets') ? 'Previous Matchup Highlights' : 'Watch Video'}>
              <YouTubeEmbed
                videoId={videoId}
                title={event.opponent || 'Event Video'}
              />
            </SectionCard>
          )}

          {/* Last Updated - hidden in guest view */}
          {!isGuestView && event.enriched_at && (
            <SectionCard title="Last Updated">
              <DetailRow
                icon={<Clock size={16} />}
                label="Date"
                value={formatDate(event.enriched_at)}
              />
            </SectionCard>
          )}
        </div>
      </>
    );
  }

  return (
    <Portal>
      {/* Mobile: Full modal with backdrop */}
      {isMobile ? (
        <div
          className="fixed inset-0 flex items-center justify-center p-0"
          style={{ zIndex: 300 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-modal-title"
          data-testid="event-detail-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Content */}
          <div className="relative bg-dark-surface flex flex-col w-full h-full">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg px-3 py-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                  <h2
                    id="event-modal-title"
                    className="font-bold text-white truncate leading-tight text-base"
                  >
                    {cleanEventName}
                  </h2>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close modal"
                data-testid="event-detail-close-button"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body - use shared content */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3">
              {renderModalContent()}
            </div>

            {/* Footer */}
            <div className="border-t border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg p-3">
              <div className="space-y-2">
                {/* Mobile Navigation */}
                {hasNavigation && (
                  <div className="flex items-center justify-center gap-3 pb-2">
                    <button
                      onClick={onPrevious}
                      disabled={!canGoPrevious}
                      className="p-3 min-w-[44px] min-h-[44px] text-dark-muted hover:text-dark-text bg-dark-input border border-dark-border/30 rounded-xl transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Previous event"
                      data-testid="event-detail-prev-button"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="text-sm text-dark-muted">
                      {currentIndex + 1} / {totalEvents}
                    </span>
                    <button
                      onClick={onNext}
                      disabled={!canGoNext}
                      className="p-3 min-w-[44px] min-h-[44px] text-dark-muted hover:text-dark-text bg-dark-input border border-dark-border/30 rounded-xl transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Next event"
                      data-testid="event-detail-next-button"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}

                {/* Mobile Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setTicketModalOpen(true)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] text-white text-sm font-semibold rounded-lg transition-all duration-150 ${
                      isFullyBooked
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-brand-500 hover:bg-brand-600'
                    }`}
                    data-testid="event-detail-ticket-button"
                  >
                    <Ticket size={18} />
                    {isFullyBooked ? 'Request Standby' : 'Request Tickets'}
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-3 min-h-[44px] bg-dark-surface/50 border border-dark-border/30 rounded-xl text-sm text-dark-text hover:bg-dark-input transition-all duration-150 font-medium"
                    data-testid="event-detail-mobile-close-button"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Desktop: Non-modal floating window - NO backdrop, draggable */
        <div
          ref={modalRef}
          className="fixed bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl shadow-2xl flex flex-col"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: '640px',
            maxHeight: '90vh',
            zIndex: 50,
          }}
          role="dialog"
          aria-modal="false"
          aria-labelledby="event-modal-title"
          data-testid="event-detail-modal"
        >
          {/* Header with drag handle */}
          <div
            className="flex items-center justify-between border-b border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg rounded-t-xl px-4 py-3 cursor-move select-none"
            onMouseDown={handleDragStart}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Drag indicator */}
              <div className="flex-shrink-0 text-dark-muted">
                <Move size={16} />
              </div>

              {/* Navigation buttons */}
              {hasNavigation && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onPrevious?.(); }}
                    disabled={!canGoPrevious}
                    className="p-1.5 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-md transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed min-w-[32px] min-h-[32px] flex items-center justify-center"
                    aria-label="Previous event"
                    title="Previous event (Left Arrow)"
                    data-testid="event-detail-prev-button"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onNext?.(); }}
                    disabled={!canGoNext}
                    className="p-1.5 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-md transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed min-w-[32px] min-h-[32px] flex items-center justify-center"
                    aria-label="Next event"
                    title="Next event (Right Arrow)"
                    data-testid="event-detail-next-button"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2
                    id="event-modal-title"
                    className="font-bold text-white truncate leading-tight text-lg"
                  >
                    {cleanEventName}
                  </h2>
                  {hasNavigation && (
                    <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-dark-muted bg-dark-surface/50 border border-dark-border/30 rounded-full">
                      {currentIndex + 1} of {totalEvents}
                    </span>
                  )}
                </div>
                <p className="text-xs text-dark-muted mt-0.5">
                  {formatDate(event.event_date)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2" onMouseDown={(e) => e.stopPropagation()}>
              {/* Ticket Request Button */}
              <button
                onClick={() => setTicketModalOpen(true)}
                className={`flex items-center gap-1.5 px-3 py-2 text-white text-sm font-medium rounded-lg transition-all duration-150 min-h-[40px] ${
                  isFullyBooked
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-brand-500 hover:bg-brand-600'
                }`}
                aria-label={isFullyBooked ? "Request standby" : "Request tickets"}
                title={isFullyBooked ? "Join standby list for this event" : "Request tickets for this event"}
                data-testid="event-detail-ticket-button"
              >
                <Ticket size={16} />
                {isFullyBooked ? 'Standby' : 'Request'}
              </button>
              {/* Panel mode toggle */}
              <button
                onClick={togglePanelMode}
                className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center"
                aria-label="Dock to right panel"
                title="Dock to right panel"
                data-testid="event-detail-toggle-mode"
              >
                <PanelRightOpen size={18} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center"
                aria-label="Close"
                data-testid="event-detail-close-button"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body - scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {renderModalContent()}
          </div>

          {/* Footer */}
          <div className="border-t border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg rounded-b-xl px-4 py-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-dark-muted">Event ID: {event.id}</p>
              <button
                onClick={onClose}
                className="px-3 py-1.5 bg-dark-surface/50 border border-dark-border/30 rounded-xl text-sm text-dark-text hover:bg-dark-input transition-all duration-150 min-h-[36px]"
                data-testid="event-detail-footer-close-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Request Modal - overlays on top of event modal */}
      <TicketRequestModal
        isOpen={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        event={event}
        allEvents={allEvents}
        isStandbyRequest={isFullyBooked}
        maxTickets={isGuestView ? maxGuestTickets : undefined}
      />
    </Portal>
  );
};

export default EventDetailModal;
